import React, { useState, useEffect } from 'react';
import { Truck, X, DollarSign, MapPin, Send, Bike, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

interface DispatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  tenantId: string;
  onSuccess: () => void;
}

const DispatchModal: React.FC<DispatchModalProps> = ({ isOpen, onClose, order, tenantId, onSuccess }) => {
  if (!isOpen || !order) return null;

  const [loading, setLoading] = useState(false);
  const [courierName, setCourierName] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [showBalanceModal, setShowBalanceModal] = useState(false);

  // Estado local para o valor como string para facilitar a digitação
  const [deliveryFeeStr, setDeliveryFeeStr] = useState('');

  // Melhor Envio States
  const [meServices, setMeServices] = useState<any[]>([]);
  const [selectedService, setSelectedService] = useState('');
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [labelResult, setLabelResult] = useState<any>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false); // Guardará URL do PDF e tracking
  const [statusMessage, setStatusMessage] = useState<{ text: string, type: 'error' | 'success' | 'info' | null }>({ text: '', type: null });

  useEffect(() => {
    setCourierName(order.courier_name || '');
    setTrackingUrl(order.tracking_url || '');

    // Converte valor do banco para string editável
    if (order.delivery_fee && parseFloat(order.delivery_fee) > 0) {
      setDeliveryFeeStr(order.delivery_fee.toString().replace('.', ','));
    } else {
      setDeliveryFeeStr('');
    }

    // Se já tiver gerado etiqueta antes, recuperar as infos do JSON delivery_info
    if (order.delivery_info?.pdf_url) {
      setLabelResult(order.delivery_info);
    }

    // [NOVO] Se o cliente já escolheu um frete no checkout, pré-selecionar aqui
    if (order.delivery_info?.service_id) {
      setSelectedService(String(order.delivery_info.service_id));
    }

    // Carregar transportadoras do Melhor Envio
    axios.get(`https://zapshop.app/api/shipping/services?tenant_id=${tenantId}`)
      .then(res => {
        if (Array.isArray(res.data)) setMeServices(res.data);
      })
      .catch(() => console.error("Sem config ME"));

  }, [order, tenantId]);

  // Efeito para buscar o preview quando o serviço muda
  useEffect(() => {
    if (!selectedService) {
      setPreviewData(null);
      return;
    }
    const fetchPreview = async () => {
      setLoadingPreview(true);
      try {
        const res = await axios.post(`https://zapshop.app/api/orders/${order.id}/melhorenvio/preview`, {
          tenant_id: tenantId,
          service_id: selectedService
        });
        setPreviewData(res.data);
      } catch (e) {
        console.error("Erro no preview", e);
      } finally {
        setLoadingPreview(false);
      }
    };
    fetchPreview();
  }, [selectedService, order.id, tenantId]);

  // Limpa mensagem de status após 5 segundos se for sucesso
  useEffect(() => {
    if (statusMessage.type === 'success') {
      const timer = setTimeout(() => setStatusMessage({ text: '', type: null }), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  // Função inteligente de tratamento de moeda
  const handleFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    // 1. Permite apenas números e vírgula/ponto
    val = val.replace(/[^0-9.,]/g, '');

    // 2. Troca ponto por vírgula para visualização brasileira
    val = val.replace('.', ',');

    // 3. Evita múltiplas vírgulas
    const parts = val.split(',');
    if (parts.length > 2) return;

    // 4. Remove zero à esquerda se não for "0,"
    if (val.length > 1 && val.startsWith('0') && val[1] !== ',') {
      val = val.substring(1);
    }

    setDeliveryFeeStr(val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Converte a string brasileira (15,50) para float (15.50) antes de enviar
      let feeToSend = 0;
      if (deliveryFeeStr) {
        feeToSend = parseFloat(deliveryFeeStr.replace(',', '.'));
      }
      if (isNaN(feeToSend)) feeToSend = 0;

      // Debug no Console do Navegador
      console.log("Enviando despacho:", {
        id: order.id,
        tenant: tenantId,
        courier: courierName,
        fee: feeToSend
      });

      await axios.put(`https://zapshop.app/api/orders/${order.id}/dispatch`, {
        tenant_id: tenantId,
        courier_name: courierName,
        tracking_url: trackingUrl,
        delivery_fee: feeToSend,
        status: 'SHIPPED'
      });

      setStatusMessage({ text: 'Pedido despachado com sucesso!', type: 'success' });
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Erro ao despachar:", error);
      setStatusMessage({ text: "Erro ao salvar: " + (error.response?.data?.error || error.message), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLabel = async () => {
    if (!selectedService) {
      setStatusMessage({ text: "Selecione um serviço de frete do Melhor Envio.", type: 'info' });
      return;
    }
    setGeneratingLabel(true);
    try {
      const res = await axios.post(`https://zapshop.app/api/orders/${order.id}/melhorenvio/label`, {
        tenant_id: tenantId,
        service_id: selectedService
      });

      setLabelResult(res.data.delivery_info);
      setStatusMessage({ text: 'Etiqueta gerada com sucesso! Abrindo arquivo...', type: 'success' });

      // Auto-abrir a etiqueta em uma nova aba
      if (res.data.delivery_info.pdf_url) {
        window.open(res.data.delivery_info.pdf_url, '_blank');
      }

      onSuccess();
      // Não fecha a modal pra ele poder imprimir
    } catch (e: any) {
      const errorMsgRaw = e.response?.data?.error || e.message;
      const errorStr = typeof errorMsgRaw === 'object' ? JSON.stringify(errorMsgRaw) : String(errorMsgRaw);

      if (errorStr.toLowerCase().includes('saldo') && errorStr.toLowerCase().includes('insuficiente')) {
        setShowBalanceModal(true);
      } else {
        setStatusMessage({ text: "Erro ao gerar etiqueta: " + (typeof errorMsgRaw === 'object' ? JSON.stringify(errorMsgRaw) : errorMsgRaw), type: 'error' });
      }
    } finally {
      setGeneratingLabel(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        <div className="bg-gray-50 p-4 border-b flex justify-between items-center">
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <Truck className="text-brand-600" size={20} />
            Despachar Pedido #{order.id.slice(0, 8)}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full p-1">
            <X size={20} />
          </button>
        </div>

        {/* NOTIFICAÇÃO CUSTOMIZADA */}
        {statusMessage.type && (
          <div className={`p-4 flex items-start gap-3 animate-fade-in ${statusMessage.type === 'error' ? 'bg-red-50 border-b border-red-100 text-red-700' :
            statusMessage.type === 'success' ? 'bg-emerald-50 border-b border-emerald-100 text-emerald-700' :
              'bg-blue-50 border-b border-blue-100 text-blue-700'
            }`}>
            {statusMessage.type === 'error' ? <XCircle size={18} className="shrink-0 mt-0.5" /> :
              statusMessage.type === 'success' ? <CheckCircle size={18} className="shrink-0 mt-0.5" /> : <AlertCircle size={18} className="shrink-0 mt-0.5" />}
            <div className="text-[11px] font-medium flex-1">{statusMessage.text}</div>
            <button onClick={() => setStatusMessage({ text: '', type: null })} className="opacity-50 hover:opacity-100 shrink-0">
              <X size={14} />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <strong>Dados de Entrega</strong><br />
            Essas informações serão enviadas ao cliente.
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Valor do Frete (R$)</label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={deliveryFeeStr}
              onChange={handleFeeChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-brand-500 outline-none font-bold text-lg text-gray-700"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome do Entregador</label>
            <div className="relative">
              <Bike className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                type="text"
                required
                placeholder="Ex: Motoboy João"
                value={courierName}
                onChange={(e) => setCourierName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 p-2.5 focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Rastreio (Opcional)</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Link ou código"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 p-2.5 focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading || generatingLabel}
              className="w-full bg-orange-600 text-white font-bold py-3 rounded-xl hover:bg-orange-700 transition-colors flex justify-center items-center gap-2 shadow-lg shadow-orange-600/20"
            >
              {loading ? 'Salvando...' : (
                <>
                  <Send size={18} /> Confirmar Envio (Manual)
                </>
              )}
            </button>
          </div>

        </form>

        {/* SECÃO MELHOR ENVIO */}
        <div className="border-t border-gray-100 bg-gray-50 p-6">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 mb-4">
            <Truck size={18} className="text-brand-600" /> Envio Inteligente (Melhor Envio)
          </h3>

          {order.delivery_info?.name && !labelResult?.pdf_url && (
            <div className="mb-4 p-3 bg-brand-50 border border-brand-100 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-brand-600" />
                <p className="text-[11px] text-brand-700">
                  O cliente escolheu: <strong>{order.delivery_info.name}</strong>
                </p>
              </div>
              <div className="flex justify-between items-center pt-1 border-t border-brand-100/50">
                <span className="text-[10px] text-brand-600 font-bold uppercase">Valor Pago pelo Cliente:</span>
                <span className="text-xs font-black text-brand-700">R$ {Number(order.delivery_info.price || 0).toFixed(2).replace('.', ',')}</span>
              </div>
              {order.delivery_info.days && (
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-brand-600 font-bold uppercase">Prazo Prometido:</span>
                  <span className="text-xs font-black text-brand-700">{order.delivery_info.days} dias úteis</span>
                </div>
              )}
            </div>
          )}

          {labelResult?.pdf_url ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center space-y-3">
              <p className="text-sm text-emerald-800 font-bold">Etiqueta Gerada com Sucesso 🎉</p>
              {labelResult.tracking_url && (
                <p className="text-xs text-emerald-700 break-all">Rastreio: <a href={labelResult.tracking_url} target="_blank" className="font-black underline">{labelResult.tracking_url}</a></p>
              )}
              <div className="flex flex-col gap-2">
                <a href={labelResult.pdf_url} target="_blank" className="inline-block w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 rounded-lg transition-colors flex items-center justify-center gap-2">
                  Visualizar Online
                </a>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(`https://zapshop.app/api/orders/${order.id}/melhorenvio/print-file`, {
                        tenant_id: tenantId,
                        shipment_id: labelResult.melhorenvio_order_id
                      });
                      if (res.data.url) window.open(res.data.url, '_blank');
                    } catch (e) { setStatusMessage({ text: "Erro ao obter arquivo.", type: 'error' }); }
                  }}
                  className="w-full bg-gray-800 hover:bg-black text-white font-bold py-2 rounded-lg transition-colors text-sm"
                >
                  Download Direto (PDF)
                </button>
              </div>
            </div>
          ) : meServices.length > 0 ? (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">O valor da etiqueta será descontado da sua carteira no Melhor Envio automaticamente.</p>
              <div>
                <select
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-brand-500 outline-none text-sm"
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                >
                  <option value="">Selecione uma transportadora...</option>
                  {meServices.map(s => (
                    <option key={s.id} value={s.id}>{s.company.name} - {s.name}</option>
                  ))}
                </select>
              </div>


              {loadingPreview && <div className="text-[10px] text-brand-600 font-bold animate-pulse">Calculando taxas e prazos...</div>}

              {previewData && !loadingPreview && (
                <div className="bg-white border-2 border-brand-100 rounded-xl p-3 flex justify-between items-center shadow-sm">
                  <div>
                    <p className="text-[10px] font-black uppercase text-gray-400">Custo do Frete</p>
                    <p className="text-lg font-black text-brand-700">R$ {Number(previewData.price || 0).toFixed(2).replace('.', ',')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-black uppercase text-gray-400">Entrega Estimada</p>
                    <p className="text-xs font-bold text-gray-700">
                      {previewData.delivery_range ? `${previewData.delivery_range.min}-${previewData.delivery_range.max}` : (previewData.delivery_time || '?')} dias úteis
                    </p>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleGenerateLabel}
                disabled={generatingLabel || !selectedService || loadingPreview}
                className="w-full bg-surface-900 text-white font-bold py-3 rounded-xl hover:bg-black transition-colors flex justify-center items-center gap-2"
              >
                {generatingLabel ? 'Processando Pagamento e PDF...' : 'Comprar e Gerar Etiqueta'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-gray-400">Integração do Melhor Envio não localizada ou loja sem token de acesso.</p>
          )}
        </div>

      </div>

      {/* MODAL DE SALDO INSUFICIENTE */}
      {showBalanceModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-in zoom-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden p-8 text-center space-y-6">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto ring-8 ring-red-50/50">
              <DollarSign size={40} className="animate-pulse" />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-gray-900">Saldo Insuficiente!</h3>
              <p className="text-sm text-gray-500">
                Você não possui saldo suficiente na sua carteira do **Melhor Envio** para emitir esta etiqueta agora.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowBalanceModal(false);
                  onClose();
                  window.location.hash = 'shipping-recharge';
                }}
                className="w-full bg-brand-600 hover:bg-brand-700 text-white font-black py-4 rounded-2xl shadow-xl shadow-brand-600/20 transition-all active:scale-95"
              >
                Adicionar Saldo Agora
              </button>

              <button
                onClick={() => setShowBalanceModal(false)}
                className="w-full bg-gray-50 hover:bg-gray-100 text-gray-500 font-bold py-3 rounded-2xl transition-all"
              >
                Voltar
              </button>
            </div>

            <p className="text-[10px] text-gray-400">
              O saldo pode ser adicionado via PIX com aprovação instantânea na aba de Logística.
            </p>
          </div>
        </div>
      )}
    </div >
  );
};

export default DispatchModal;
